import { useState, useEffect, useRef, useCallback } from 'react';
import type { ProjectDetail, PhotoItem, Collector } from '@/types/odoo';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import { listCollectorPhotos, uploadCollectorPhoto } from '@/api/tasks';
import { compressImage } from '@/utils/imageCompression';
import { buildBlokGroups, getCollectorFotoLabel, getCollectorMeta } from '@/utils/collectors';
import { InstallateurTaakContent } from './InstallateurTaakContent';

interface InstallateurTaakTabProps {
  projectId: number;
  projectName: string;
  detail: ProjectDetail;
}

/* ─── Section 4: Collectoren Overzicht ─── */

function CollectorenOverzichtSection({
  projectId,
  projectName,
}: {
  projectId: number;
  projectName: string;
}) {
  const {
    collectors,
    collectorInstallerNames,
    collectorProjectId,
    collectorLoading,
    collectorError,
    fetchCollectoren,
  } = useProjectViewStore();

  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canUploadPhoto = canFeature('photo_upload');

  const [expandedBlok, setExpandedBlok] = useState<string | null>(null);
  const [expandedVerd, setExpandedVerd] = useState<string | null>(null);
  const [expandedCollector, setExpandedCollector] = useState<number | null>(null);

  useEffect(() => {
    if ((collectors === null || collectorProjectId !== projectId) && !collectorLoading) {
      fetchCollectoren(projectId);
    }
  }, [collectors, collectorProjectId, collectorLoading, projectId, fetchCollectoren]);

  if (collectorLoading && !collectors) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Collectoren Overzicht</h3>
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
        </div>
      </section>
    );
  }

  if (collectorError) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Collectoren Overzicht</h3>
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{collectorError}</div>
      </section>
    );
  }

  if (!collectors || collectors.length === 0) {
    return (
      <section className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-extrabold text-td-navy mb-4">Collectoren Overzicht</h3>
        <p className="text-sm text-td-muted italic">Geen collectoren gevonden</p>
      </section>
    );
  }

  const blokGroups = buildBlokGroups(collectors);
  const installerNames = collectorInstallerNames || {};

  // Auto-expand first blok
  const firstBlok = blokGroups[0]?.blok ?? null;
  const activeBlok = expandedBlok ?? firstBlok;

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-extrabold text-td-navy mb-1">Collectoren Overzicht</h3>
      <p className="text-sm text-td-muted mb-4">
        {collectors.length} collectoren in {blokGroups.length} {blokGroups.length === 1 ? 'blok' : 'blokken'}
      </p>

      <div className="space-y-2">
        {blokGroups.map((group) => {
          const isBlokOpen = activeBlok === group.blok;
          return (
            <div key={group.blok}>
              <button
                type="button"
                onClick={() => setExpandedBlok(isBlokOpen ? null : group.blok)}
                className="w-full text-left rounded-lg border border-gray-100 bg-gray-50 hover:border-td-blue/30 p-3 transition-all"
              >
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-4 h-4 text-td-muted shrink-0 transition-transform ${isBlokOpen ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-bold text-td-navy flex-1">Blok {group.blok}</span>
                  <span className="text-[10px] font-bold text-td-muted bg-gray-200 px-1.5 py-0.5 rounded">
                    {group.totalCollectors}
                  </span>
                </div>
              </button>

              {isBlokOpen && (
                <div className="mt-1.5 ml-4 space-y-1.5">
                  {group.verdiepingen.map((vg, vIdx) => {
                    const verdKey = `${group.blok}-${vg.verdieping}`;
                    const firstVerd = `${group.blok}-${group.verdiepingen[0]?.verdieping}`;
                    const isVerdOpen = (expandedVerd ?? firstVerd) === verdKey;

                    return (
                      <div key={vg.verdieping}>
                        <button
                          type="button"
                          onClick={() => setExpandedVerd(isVerdOpen ? null : verdKey)}
                          className="w-full text-left rounded-lg border border-gray-100 bg-white hover:border-td-blue/30 px-3 py-2 transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className={`w-3.5 h-3.5 text-td-muted shrink-0 transition-transform ${isVerdOpen ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span className="text-sm font-bold text-td-navy">Verdieping {vg.verdieping}</span>
                            <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded ml-auto">
                              {vg.collectors.length}
                            </span>
                          </div>
                        </button>

                        {isVerdOpen && (
                          <div className="mt-1.5 ml-4 space-y-1.5">
                            {vg.collectors.map((collector) => (
                              <OverzichtCollectorCard
                                key={collector.id}
                                collector={collector}
                                installerNames={installerNames}
                                projectName={projectName}
                                projectId={projectId}
                                isExpanded={expandedCollector === collector.id}
                                onToggle={() => setExpandedCollector(expandedCollector === collector.id ? null : collector.id)}
                                canUploadPhoto={canUploadPhoto}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function OverzichtCollectorCard({
  collector,
  installerNames,
  projectName,
  projectId,
  isExpanded,
  onToggle,
  canUploadPhoto,
}: {
  collector: Collector;
  installerNames: Record<string, string>;
  projectName: string;
  projectId: number;
  isExpanded: boolean;
  onToggle: () => void;
  canUploadPhoto: boolean;
}) {
  const fotoLabel = getCollectorFotoLabel(collector);

  return (
    <div className={`rounded-lg border ${isExpanded ? 'border-td-blue/30 shadow-sm' : 'border-gray-200'} bg-white transition-all`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-td-navy truncate flex-1 min-w-0">{collector.name}</span>
          <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
            {collector.x_studio_collector_m2} m²
          </span>
          {collector.x_studio_collector_systeem && (
            <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
              {collector.x_studio_collector_systeem}
            </span>
          )}
          {fotoLabel && (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ${
              fotoLabel === 'Goedgekeurd' ? 'bg-green-100 text-green-700' :
              fotoLabel === 'Geupload' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-td-muted'
            }`}>
              {fotoLabel}
            </span>
          )}
          <svg
            className={`w-3.5 h-3.5 text-gray-300 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 p-3">
          {canUploadPhoto ? (
            <OverzichtPhotoUpload collector={collector} projectName={projectName} projectId={projectId} />
          ) : (
            <OverzichtPhotoReadonly collector={collector} projectName={projectName} />
          )}
        </div>
      )}
    </div>
  );
}

function OverzichtPhotoUpload({
  collector,
  projectName,
  projectId,
}: {
  collector: Collector;
  projectName: string;
  projectId: number;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const meta = getCollectorMeta(collector, projectName);

  const loadPhotos = useCallback(async () => {
    try {
      const r = await listCollectorPhotos(collector.id, meta);
      setPhotos(r.photos);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collector.id]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        const compressed = await compressImage(file);
        await uploadCollectorPhoto(collector.id, compressed, meta);
      }
      await loadPhotos();
      useProjectViewStore.getState().fetchCollectoren(projectId);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  // PhotoLightbox is an image viewer; a PDF opens in a new tab instead.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  return (
    <div>
      <p className="text-xs font-bold text-td-muted uppercase tracking-wide mb-2">Foto&apos;s</p>
      <div className="flex flex-wrap gap-2 items-center">
        {loading && (
          <div className="w-16 h-16 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Foto's laden...</span>
            </div>
          </div>
        )}
        {!loading && photos.map((p) => (
          <PhotoGridThumb
            key={p.key}
            photo={p}
            tileSizeClass="w-16 h-16"
            imgClassName="w-16 h-16 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors"
            onOpen={() => { setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key)); setLightboxOpen(true); }}
          />
        ))}
        {!loading && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-td-blue transition-colors disabled:opacity-50"
            aria-label="Foto uploaden"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
                <span className="sr-only">Uploaden...</span>
              </div>
            ) : (
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          multiple
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
          className="hidden"
          aria-label="Selecteer foto's om te uploaden"
        />
      </div>
      {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

function OverzichtPhotoReadonly({
  collector,
  projectName,
}: {
  collector: Collector;
  projectName: string;
}) {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const meta = getCollectorMeta(collector, projectName);

  useEffect(() => {
    listCollectorPhotos(collector.id, meta)
      .then((r) => setPhotos(r.photos))
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collector.id]);

  const fotoLabel = getCollectorFotoLabel(collector) ?? 'Geen Fotos';

  // PhotoLightbox is an image viewer; a PDF opens in a new tab instead.
  const imagePhotos = photos.filter((p) => !isPdfPhoto(p));

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-bold text-td-muted uppercase tracking-wide">Foto&apos;s</p>
        <span className="text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
          {loading ? '...' : photos.length}
        </span>
      </div>
      {loading && (
        <div className="w-16 h-16 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Foto's laden...</span>
          </div>
        </div>
      )}
      {!loading && photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <PhotoGridThumb
              key={p.key}
              photo={p}
              tileSizeClass="w-16 h-16"
              imgClassName="w-16 h-16 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors"
              onOpen={() => { setLightboxIndex(imagePhotos.findIndex((i) => i.key === p.key)); setLightboxOpen(true); }}
            />
          ))}
        </div>
      )}
      {!loading && photos.length === 0 && (
        <p className="text-xs text-td-muted italic">Geen foto&apos;s</p>
      )}
      {lightboxOpen && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
/* ─── Main Tab Component ─── */

export function InstallateurTaakTab({ projectId, projectName, detail }: InstallateurTaakTabProps) {
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-extrabold text-td-navy">Installateur Taak</h2>
        <p className="text-sm text-td-muted mt-0.5">Overzicht voor installateurs</p>
      </div>

      <InstallateurTaakContent projectId={projectId} projectName={projectName} detail={detail} />
      <CollectorenOverzichtSection projectId={projectId} projectName={projectName} />
    </div>
  );
}
